import { ensureToken } from "./guard";
import { canonicalSkin } from "../shared/defs";

export interface MpPlayer {
  id: number;
  name: string;
  color: number;
  kills: number;
  deaths: number;
}

export interface Match {
  state: "lobby" | "play" | "over";
  v: number;
  url: string;
  title: string;
  target: number;
  winner: number;
  pub?: { map: string; until: number; banned?: string[]; orient?: "portrait" | "landscape" };
}

export interface Snap {
  t: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  aim: number;
  facing: number;
  grounded: boolean;
  jet: number;
  tumble: number;
  weapon: number;
  hp: number;
  alive: boolean;
  skin: string;
  name: string;
  dx?: number;
  dy?: number;
  da?: number;
  drotor?: number;
}

type Handler = (msg: Record<string, unknown>) => void;

export class RoomClient {
  ws: WebSocket | null = null;
  code = "";
  id = 0;
  key = "";
  hostId = 0;
  players: MpPlayer[] = [];
  match: Match | null = null;
  snaps = new Map<number, Snap[]>();
  feed: Array<{ k: number; v: number; w: string }> = [];
  onEvent: Handler | null = null;
  private ping: number | null = null;
  private generation = 0;

  get connected() {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  get quick() {
    return !!this.match?.pub;
  }

  async connect(code: string, name: string): Promise<void> {
    await ensureToken();
    const attempt = this.generation + 1;
    try {
      return await this.open(code, name);
    } catch (err) {
      // A token from before a network switch is refused at the upgrade. Renew once
      // and retry, unless another connect or close happened meanwhile.
      if (this.generation !== attempt || this.code !== code) throw err;
      await ensureToken(true);
      if (this.generation !== attempt) throw err;
      return this.open(code, name);
    }
  }

  private open(code: string, name: string): Promise<void> {
    this.close();
    this.code = code;
    const generation = ++this.generation;
    const proto = location.protocol === "https:" ? "wss" : "ws";
    const ws = new WebSocket(`${proto}://${location.host}/mp/${code}/ws?name=${encodeURIComponent(name)}`);
    this.ws = ws;
    return new Promise((resolve, reject) => {
      let opened = false;
      const timer = window.setTimeout(() => reject(new Error("The room did not answer.")), 8000);
      const ready = () => {
        if (opened) return;
        opened = true;
        window.clearTimeout(timer);
        // Exactly '{"t":"ping"}': the room answers it without waking up (see PING_MSG in room.ts).
        this.ping = window.setInterval(() => this.send({ t: "ping" }), 2000);
        resolve();
      };
      ws.addEventListener("error", () => {
        window.clearTimeout(timer);
        if (!opened) reject(new Error("Could not join that room."));
      });
      ws.addEventListener("message", (ev) => {
        if (generation !== this.generation) return;
        let msg: Record<string, unknown>;
        try {
          msg = JSON.parse(String(ev.data)) as Record<string, unknown>;
        } catch {
          return;
        }
        this.take(msg);
        if (msg.t === "welcome" || msg.t === "err") ready();
        this.onEvent?.(msg);
      });
      ws.addEventListener("close", () => {
        if (generation === this.generation) this.onEvent?.({ t: "closed" });
      });
    });
  }

  close() {
    this.generation++;
    if (this.ping) window.clearInterval(this.ping);
    this.ping = null;
    this.ws?.close();
    this.ws = null;
    this.snaps.clear();
  }

  send(msg: unknown) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  pushSnap(id: number, snap: Snap) {
    const list = this.snaps.get(id) ?? [];
    list.push(snap);
    if (list.length > 40) list.splice(0, list.length - 40);
    this.snaps.set(id, list);
  }

  sample(id: number, now: number): Snap | null {
    const list = this.snaps.get(id);
    if (!list?.length) return null;
    const when = now - 100;
    let prev = list[0];
    let next = list[list.length - 1];
    for (let i = 0; i < list.length; i++) {
      if (list[i].t <= when) prev = list[i];
      if (list[i].t >= when) {
        next = list[i];
        break;
      }
    }
    const span = Math.max(1, next.t - prev.t);
    const u = Math.max(0, Math.min(1, (when - prev.t) / span));
    return {
      ...next,
      x: prev.x + (next.x - prev.x) * u,
      y: prev.y + (next.y - prev.y) * u,
      vx: next.vx,
      vy: next.vy,
      tumble: next.tumble,
    };
  }

  private take(msg: Record<string, unknown>) {
    if (msg.t === "welcome") {
      this.id = Number(msg.id) || 0;
      this.key = String(msg.key || "");
      this.hostId = Number(msg.host) || 0;
      this.players = (msg.players as MpPlayer[]) || [];
      this.match = (msg.match as Match) || null;
    } else if (msg.t === "join") {
      const player = msg.p as MpPlayer;
      if (player && !this.players.some((p) => p.id === player.id)) this.players.push(player);
    } else if (msg.t === "leave") {
      const id = Number(msg.id);
      this.players = this.players.filter((p) => p.id !== id);
      this.snaps.delete(id);
    } else if (msg.t === "host") this.hostId = Number(msg.id) || 0;
    else if (msg.t === "name") {
      const player = this.players.find((p) => p.id === Number(msg.id));
      if (player) player.name = String(msg.name || player.name);
    } else if (msg.t === "score") this.players = (msg.players as MpPlayer[]) || this.players;
    else if (msg.t === "match" || msg.t === "level") {
      if (msg.match) this.match = msg.match as Match;
    } else if (msg.t === "feed") {
      this.feed.unshift({ k: Number(msg.k), v: Number(msg.v), w: String(msg.w || "") });
      this.feed.length = Math.min(6, this.feed.length);
    } else if (msg.t === "s" && msg.s && typeof msg.s === "object") {
      const s = msg.s as Record<string, unknown>;
      const id = Number(msg.id);
      this.pushSnap(id, {
        t: performance.now(),
        x: Number(s.x) || 0,
        y: Number(s.y) || 0,
        vx: Number(s.vx) || 0,
        vy: Number(s.vy) || 0,
        aim: Number(s.aim) || 0,
        facing: Number(s.facing) || 1,
        grounded: !!s.g,
        jet: Number(s.jet) || 0,
        tumble: Number(s.tb) || 0,
        weapon: Number(s.w) || 0,
        hp: Number(s.hp) || 0,
        alive: s.alive !== false,
        skin: canonicalSkin(String(s.skin || "stickman")),
        name: String(s.name || ""),
        dx: Number.isFinite(Number(s.dx)) ? Number(s.dx) : undefined,
        dy: Number.isFinite(Number(s.dy)) ? Number(s.dy) : undefined,
        da: Number.isFinite(Number(s.da)) ? Number(s.da) : undefined,
        drotor: Number.isFinite(Number(s.drotor)) ? Number(s.drotor) : undefined,
      });
    }
  }
}
